import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Info } from "lucide-react";
import { fmt } from "@/lib/format";

// ── TPE chart palette (restrained; no neon / rainbow) ──────────────────────
export const C = { green: "#164C3B", emerald: "#2F6B57", sage: "#7FA491", champagne: "#C9A86A", grid: "#F0EBE2", axis: "#A89A88" };

const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };
const monthKey = (iso) => { try { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; } catch { return ""; } };
const monthLabel = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short" }); } catch { return ""; } };

// Deliberate insufficient-data state — never fabricate history to fill a chart.
export function InsufficientData({ children = "Not enough confirmed history yet to chart this. It builds as you confirm Edits.", testid }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-[#E0D8C9] bg-[#FCFBF8] px-4 py-5 text-sm text-[#8A7C6B]" data-testid={testid}>
      <Info className="h-4 w-4 shrink-0 text-[#C9A86A]" /> {children}
    </div>
  );
}

const tip = { borderRadius: 10, border: "1px solid #E5E0D9", fontSize: 13, boxShadow: "0 8px 24px -16px rgba(22,76,59,.4)" };

// Build a CHRONOLOGICAL copy (never mutate stored history) with a running total.
export function buildCumulative(history, predicate) {
  const rows = (history || []).filter(predicate ? predicate : () => true)
    .map((h) => ({ date: h.date, amount: h.amount_confirmed || 0 }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  let run = 0;
  return rows.map((r) => { run += r.amount; return { x: fmtShort(r.date), amount: r.amount, cumulative: run }; });
}

const axisY = (v) => `$${v >= 1000 ? (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + "k" : v}`;

// VISUAL: cumulative confirmed dollars over time. Q: "Am I making progress?"
// Options: daily=aggregate same-day Edits into one point (Home); bySequence=label
// x-axis "Start, Edit 1, Edit 2..." instead of dates (My Goal contribution history).
export function ConfirmedOverTime({ history, testid = "chart-cumulative", color = C.green, label = "Confirmed to date", daily = false, bySequence = false, startPoint = bySequence, amountLabel = "" }) {
  const sorted = (history || []).map((h) => ({ date: h.date, amount: h.amount_confirmed || 0 }))
    .sort((a, b) => new Date(a.date) - new Date(b.date)); // copy, never mutate source
  let rows = sorted;
  if (daily) {
    const m = new Map();
    sorted.forEach((r) => { const k = (r.date || "").slice(0, 10); const e = m.get(k) || { date: r.date, amount: 0 }; e.amount += r.amount; m.set(k, e); });
    rows = [...m.values()];
  }
  let run = 0;
  let data = rows.map((r, i) => { run += r.amount; return { x: bySequence ? `Edit ${i + 1}` : fmtShort(r.date), rawDate: r.date, amount: r.amount, cumulative: run }; });
  if (bySequence && startPoint) data = [{ x: "Start", rawDate: null, amount: 0, cumulative: 0 }, ...data];
  if (data.length < 2) return <InsufficientData testid={`${testid}-empty`} />;
  const gid = `g_${testid}`;
  const TT = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div style={{ ...tip, background: "#fff", padding: "8px 12px" }}>
        <div style={{ fontWeight: 600, color: C.green }}>{p.x}{p.rawDate ? ` · ${fmtShort(p.rawDate)}` : ""}</div>
        {p.rawDate && <div style={{ color: "#8A7C6B" }}>{daily ? "Allocated that day" : (amountLabel || "Contribution")}: {fmt(p.amount)}</div>}
        <div style={{ color: "#525252" }}>{label}: {fmt(p.cumulative)}</div>
      </div>
    );
  };
  return (
    <div className="h-64 w-full" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 10, left: -6, bottom: 0 }}>
          <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.26} /><stop offset="100%" stopColor={color} stopOpacity={0.02} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="x" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} minTickGap={bySequence ? 4 : 24} />
          <YAxis tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={52} tickFormatter={axisY} />
          <Tooltip content={<TT />} />
          <Area type="linear" dataKey="cumulative" name="cumulative" stroke={color} strokeWidth={2.5} fill={`url(#${gid})`} dot={{ r: 2.5, fill: color }} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// VISUAL: confirmed allocation across Protect / Eliminate / Build (stacked bar +
// legend). Q: "Where has my confirmed money gone?" CONFIRMED moves, not spending.
export function AllocationDistribution({ history, protection, testid = "chart-distribution" }) {
  const seg = { protection: 0, debt: 0, build: 0 };
  (history || []).forEach((h) => { if (seg[h.category] != null) seg[h.category] += h.amount_confirmed || 0; });
  const total = seg.protection + seg.debt + seg.build;
  if (total <= 0) return <InsufficientData testid={`${testid}-empty`}>No confirmed moves to break down yet.</InsufficientData>;
  const parts = [
    { key: "protection", label: "Protect", value: seg.protection, color: C.champagne },
    { key: "debt", label: "Eliminate", value: seg.debt, color: C.emerald },
    { key: "build", label: "Build", value: seg.build, color: C.green },
  ].filter((p) => p.value > 0);
  // Zero-value protection context (never imply "covered" unless current data supports it).
  const protCovered = protection && protection.state && protection.state !== "below";
  const showProtCoveredNote = seg.protection === 0 && protCovered;
  const showProtZeroRow = seg.protection === 0 && !protCovered;
  return (
    <div data-testid={testid}>
      <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-[#F0EBE2]">
        {parts.map((p) => <div key={p.key} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} title={`${p.label} ${fmt(p.value)}`} />)}
      </div>
      <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-3">
        {parts.map((p) => (
          <li key={p.key} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 text-[#525252]"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.label}</span>
            <span className="font-medium text-[#171717] tabular-nums">{fmt(p.value)} <span className="text-xs text-[#A89A88]">({Math.round((p.value / total) * 100)}%)</span></span>
          </li>
        ))}
        {showProtZeroRow && (
          <li className="flex items-center justify-between gap-2 text-sm" data-testid="allocation-protect-zero">
            <span className="flex items-center gap-2 text-[#A89A88]"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.champagne }} />Protect</span>
            <span className="font-medium text-[#A89A88] tabular-nums">{fmt(0)} <span className="text-xs">(0%)</span></span>
          </li>
        )}
      </ul>
      {showProtCoveredNote && <p className="mt-3 text-xs text-[#8A7C6B]" data-testid="allocation-protect-covered">Protection was already covered. No confirmed Edits were allocated to protection.</p>}
    </div>
  );
}

// VISUAL: confirmed Edit activity by month. Q: "How consistently am I editing?"
export function EditActivity({ history, testid = "chart-activity" }) {
  const map = new Map();
  (history || []).forEach((h) => { const k = monthKey(h.date); if (!k) return; const e = map.get(k) || { k, label: monthLabel(h.date), count: 0 }; e.count += 1; map.set(k, e); });
  const data = [...map.values()].sort((a, b) => (a.k < b.k ? -1 : 1));
  if (data.length < 2) return <InsufficientData testid={`${testid}-empty`}>Edit activity appears once you have confirmed Edits across more than one month.</InsufficientData>;
  return (
    <div className="h-44 w-full" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.axis }} axisLine={false} tickLine={false} width={34} />
          <Tooltip contentStyle={tip} formatter={(v) => [v, "Confirmed Edits"]} cursor={{ fill: "rgba(47,107,87,.06)" }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={36}>{data.map((d) => <Cell key={d.k} fill={C.emerald} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Sophisticated horizontal target gauge (metric support, not a replacement for
// the progression chart). Q: "How close am I to my goal?"
export function HorizontalGauge({ value, target, pct, color = C.emerald, track = "#EDE7DC", testid = "goal-gauge" }) {
  const p = Math.max(0, Math.min(100, pct != null ? pct : target > 0 ? Math.round((value / target) * 100) : 0));
  return (
    <div data-testid={testid}>
      <div className="flex items-end justify-between text-sm">
        <span className="font-serif text-xl text-[#164C3B] tabular-nums">{fmt(value)}</span>
        {target > 0 && <span className="text-[#8A7C6B] tabular-nums">{fmt(target)}</span>}
      </div>
      <div className="mt-2 h-3 w-full overflow-hidden rounded-full" style={{ background: track }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: color }} />
      </div>
      <p className="mt-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">{p}% funded</p>
    </div>
  );
}
